import { Box, Skeleton, Stack } from "@mui/material";

/**
 * PipelineSkeleton — run rail + DAG node boxes + console area,
 * matching the Pipeline tab layout.
 */
export function PipelineSkeleton(): React.ReactElement {
  const cardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "12px",
    bgcolor: "background.paper",
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "320px 1fr",
        gap: 2,
        alignItems: "start",
      }}
    >
      {/* Run rail skeleton */}
      <Box sx={{ ...cardSx, overflow: "hidden" }}>
        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Skeleton variant="text" width={100} height={14} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              p: 1.5,
              borderBottom: i < 3 ? "1px solid" : "none",
              borderColor: "divider",
              display: "flex",
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <Skeleton variant="circular" width={18} height={18} />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                width="80%"
                height={14}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="60%" height={12} />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Detail column skeleton */}
      <Stack spacing={2}>
        {/* Run header */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={14} />
        </Box>

        {/* DAG */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Skeleton variant="text" width={80} height={14} sx={{ mb: 2 }} />
          <Stack direction="row" spacing={3.5} alignItems="stretch">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={96}
                height={74}
                sx={{ borderRadius: "10px" }}
              />
            ))}
          </Stack>
        </Box>

        {/* Console */}
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: 1.75 }}
          >
            <Skeleton variant="text" width={120} height={14} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="rounded" width={80} height={20} />
          </Stack>
          <Skeleton
            variant="rounded"
            width="100%"
            height={120}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default PipelineSkeleton;
